import React, { useState, useEffect, type SetStateAction, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import {
  FBC_PARAM,
  FBP_PARAM,
  FB_PIXEL_ID,
  GOOGLE_MEASUREMENT_ID,
  OPENAI_PIXEL_ID,
  TIKTOK_PIXEL_ID,
  TTP_PARAM,
} from '../constants';
import { getOppref } from '../../analytics/openai';
import { selectEventsData } from '../store/events';
import { selectFlowType, selectLanding } from '../store/plans';
import { selectEmailConsent, selectUuid } from '../store/signup';
import { resolveGoogleAdsAccountId } from '../utils';

const getGoogleClientID = (setFn: React.Dispatch<SetStateAction<null | string>>) => {
  if (!GOOGLE_MEASUREMENT_ID) return;

  let isCancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;
  const readClientId = () => {
    const gtag = window.gtag;
    if (typeof gtag !== 'function') {
      attempts += 1;
      if (attempts < 20) {
        timer = setTimeout(readClientId, 250);
      }
      return;
    }
    gtag('get', String(GOOGLE_MEASUREMENT_ID), 'client_id', (clientId?: string) => {
      if (!isCancelled && clientId) {
        setFn(clientId);
      }
    });
  };
  readClientId();
  return () => {
    isCancelled = true;
    if (timer) {
      clearTimeout(timer);
    }
  };
};

export const useAnalyticsData = () => {
  const [data, setData] = useState({});
  const [cid, setCid] = useState<null | string>(null);
  const eventsData = useSelector(selectEventsData);
  const flowType = useSelector(selectFlowType);
  const landing = useSelector(selectLanding);
  const userUuid = useSelector(selectUuid);
  const consentEmail = useSelector(selectEmailConsent);
  const { fbclid, ttclid, gclid, appDomain, utm } = eventsData;
  const utmAccount = utm?.utm_account;
  const AFUserID = Cookies.get('afUserId');
  const fbc = Cookies.get(FBC_PARAM);
  const fbp = Cookies.get(FBP_PARAM);
  const ttp = Cookies.get(TTP_PARAM);
  const oppref = getOppref() || eventsData.oppref;

  const activePixelData = useMemo(() => {
    return fbclid ? { fb_pixel_id: FB_PIXEL_ID } : ttclid ? { tt_pixel_id: TIKTOK_PIXEL_ID } : {};
  }, [fbclid, ttclid]);
  const activePixelUserData = useMemo(() => {
    return fbclid
      ? { fbc, fbp, fb_click_id: fbc || fbclid || '' }
      : ttclid
        ? { ttp, tt_click_id: ttclid || '' }
        : {};
  }, [fbc, fbp, fbclid, ttclid, ttp]);

  const encodedSource = encodeURIComponent(appDomain);

  useEffect(() => {
    return getGoogleClientID(setCid);
  }, []);

  useEffect(() => {
    setData({
      appsflyer_id: AFUserID,
      google_tracking_id: GOOGLE_MEASUREMENT_ID,
      google_client_id: cid || '',
      google_ads_account_id: resolveGoogleAdsAccountId({ flowType, utmAccount }),
      ...activePixelData,
      openai_pixel_id: oppref ? OPENAI_PIXEL_ID : undefined,
      user_data: {
        uuid: userUuid,
        ...activePixelUserData,
        oppref,
        source_url: encodedSource,
        google_click_id: gclid || '',
        consent_email: consentEmail,
      },
    });
  }, [
    cid,
    AFUserID,
    flowType,
    utmAccount,
    landing,
    userUuid,
    gclid,
    encodedSource,
    activePixelData,
    activePixelUserData,
    consentEmail,
    oppref,
  ]);

  return data;
};
